/**
 * useInsuranceNavGroup — the shells' view of the insurance section.
 *
 * Maps the signed-in role to its insurance persona and that persona's
 * sections (from the section's own nav registry) as ready-to-render
 * sidebar links. Returns null when the role has no insurance persona
 * (the group simply doesn't render).
 *
 * This is shell chrome: render it in the patient design language, NOT
 * the insurance section's (see src/insurance/CLAUDE.md scope guard).
 */

import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { INSURANCE_NAV } from '@/insurance/lib/nav';
import { personaForRole, type Persona } from '@/insurance/lib/persona';

export interface InsuranceNavGroup {
  /** The persona whose sections are being shown (see `own` for admins). */
  persona: Persona;
  /** The signed-in user's own persona — 'admin' unlocks the switcher. */
  own: Persona;
  /** Bare section path (/insurance/<persona>) — the group's landing. */
  base: string;
  items: Array<{
    to: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }>;
}

export function useInsuranceNavGroup(): InsuranceNavGroup | null {
  const { role } = useAuth();
  const { pathname } = useLocation();
  const own = personaForRole(role);
  if (!own) return null;
  // Super-user (god mode): the sidebar follows whichever dashboard is
  // being viewed, so switching personas swaps the section list too.
  let persona = own;
  if (own === 'admin' && pathname.startsWith('/insurance/')) {
    const seg = pathname.split('/')[2];
    if (seg && seg in INSURANCE_NAV) persona = seg as Persona;
  }
  const base = `/insurance/${persona}`;
  return {
    persona,
    own,
    base,
    items: INSURANCE_NAV[persona].map((s) => ({
      to: `${base}/${s.key}`,
      label: s.label,
      icon: s.icon,
    })),
  };
}

/** Personas the super-user switcher can jump between (admin god mode). */
export const ADMIN_SWITCHER: Array<{ persona: Persona; label: string }> = [
  { persona: 'admin', label: 'Leads' },
  { persona: 'patient', label: 'Patient' },
  { persona: 'employer', label: 'Employer' },
  { persona: 'hospital', label: 'Hospital' },
  { persona: 'insurer', label: 'Insurer' },
  { persona: 'tpa', label: 'TPA' },
  { persona: 'superadmin', label: 'CardioQ Admin' },
];
